import { useTheme } from "styled-components";
import FormContainer from "../../components/formContainer/FormContainer";
import InputWrapper from "../../components/input/wrapper/InputWrapper";
import OverlayLoader from "../../components/loader/OverlayLoader/OverlayLoader";
import Select from "../../components/select/main/Select";
import { useLanguageContext } from "../../context/LanguageContext";
import EVAApiHelper from "../../helpers/EVAApiHelper";
import { StyledInput } from "../../styles/InputStyles";
import LocationFormSelect from "../../components/select/custom/location/form/LocationFormSelect";
import Checkbox from "../../components/input/checkbox/Checkbox";
import {
  StyledFlexColumn,
  StyledFlexItem,
  StyledFlexRow,
} from "../../styles/LayoutStyles";
import {
  StyledAltIconButton,
  StyledIconButton,
} from "../../styles/ButtonStyles";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCopy,
  faRefresh,
  faSignOut,
} from "@fortawesome/free-solid-svg-icons";
import Version from "../../components/version/Version";
import { useAuthContext } from "../../context/AuthContext";
import {
  OutlookNoPermission,
  OutlookVersionWrapper,
} from "./OutlookPageStyles";
import LanguageFormSelect from "../../components/select/custom/language/form/LanguageFormSelect";
import FlowFieldInput from "../../components/input/flow/field/FlowFieldInput";

function OutlookPageView({
  visitorTypes,
  employees,
  checkedAttendeesCount,
  fetchOutlookData,
  onVisitorTypeChange,
  onLanguageChange,
  onLocationIdChange,
  onCheckboxChange,
  onInvitationChange,
  onEmployeeChange,
  onFieldChange,
  onCopyData,
  onReturningChange,
  onExpectedEndDateChange,
  appointmentData,
  getStepsForVisitorType,
  onSubmit,
  submitError,
}) {
  const { translate, translateEntity, activeLanguages } = useLanguageContext();
  const theme = useTheme();
  const authContext = useAuthContext();

  return (
    <OverlayLoader groups="outlook_page_fetch">
      {authContext.hasPermission("outlook.read.screen") ? (
        <FormContainer
          onSubmit={onSubmit}
          submitError={submitError}
          submitLoadingGroup={"outlook_page_save"}
          header={
            <StyledFlexRow>
              <StyledFlexItem>
                <h2>{translate("eva.main.general.data")}</h2>
              </StyledFlexItem>

              <StyledIconButton onClick={fetchOutlookData}>
                <FontAwesomeIcon icon={faRefresh} />
              </StyledIconButton>
            </StyledFlexRow>
          }
        >
          <InputWrapper
            label={translate("eva.main.entities.location")}
            error={EVAApiHelper.getResponseError("location_id", submitError)}
            margin={`0px 0px ${theme.spacing.normal}`}
          >
            <LocationFormSelect
              value={appointmentData.location_id}
              onChange={onLocationIdChange}
              useNullOption={false}
            />
          </InputWrapper>

          <InputWrapper
            label={translate("eva.main.input.types.date")}
            error={EVAApiHelper.getResponseError("date", submitError)}
            margin={`0px 0px ${theme.spacing.normal}`}
          >
            <StyledInput
              type="text"
              value={
                appointmentData.expected_date +
                " " +
                appointmentData.expected_time
              }
              disabled
            />
          </InputWrapper>

          <Checkbox
            label={translate("eva.main.validation.attributes.send_invite_mail")}
            value={appointmentData.sendInvitation}
            onChange={onInvitationChange}
          />

          <StyledFlexColumn style={{ marginTop: theme.spacing.large }}>
            <InputWrapper
              label={translate("eva.main.validation.attributes.visitors") + ":"}
              style={{ marginTop: theme.spacing.normal }}
            />

            <hr style={{ marginBottom: theme.spacing.normal }} />

            {appointmentData.attendees.map((attendee, attendeeIndex) => (
              <StyledFlexColumn key={attendeeIndex}>
                <Checkbox
                  label={attendee.email}
                  value={attendee.checked}
                  onChange={() => onCheckboxChange(attendeeIndex)}
                />

                {attendee.checked && (
                  <>
                    {/* Language */}
                    {activeLanguages.length > 1 && (
                      <InputWrapper
                        label={translate("eva.main.entities.language")}
                        error={EVAApiHelper.getResponseError(
                          "language_id",
                          submitError,
                        )}
                        required={true}
                      >
                        <StyledFlexRow>
                          <StyledFlexItem>
                            <LanguageFormSelect
                              value={attendee.language_id}
                              onChange={(value) =>
                                onLanguageChange(value, attendeeIndex)
                              }
                            />
                          </StyledFlexItem>

                          {checkedAttendeesCount > 1 && (
                            <StyledAltIconButton
                              onClick={() =>
                                onCopyData("language_id", attendee.language_id)
                              }
                              title={translate("eva.main.general.copy")}
                            >
                              <FontAwesomeIcon icon={faCopy} />
                            </StyledAltIconButton>
                          )}
                        </StyledFlexRow>
                      </InputWrapper>
                    )}

                    {/* Visitor Type */}
                    <InputWrapper
                      label={translate("eva.main.entities.visitor_type")}
                      error={EVAApiHelper.getResponseError(
                        "visitor_type_id",
                        submitError,
                      )}
                      required
                    >
                      <StyledFlexRow>
                        <StyledFlexItem>
                          <Select
                            options={visitorTypes
                              .filter(
                                (visitorType) =>
                                  visitorType.location ===
                                    appointmentData.location_id ||
                                  !visitorType.location,
                              )
                              .map((visitorType) => ({
                                value: visitorType.id,
                                label: translateEntity(visitorType, "label"),
                              }))}
                            nullable={false}
                            value={attendee.visitor_type_id}
                            onChange={(value) =>
                              onVisitorTypeChange(value, attendeeIndex)
                            }
                          />
                        </StyledFlexItem>

                        {checkedAttendeesCount > 1 && (
                          <StyledAltIconButton
                            onClick={() =>
                              onCopyData(
                                "visitor_type_id",
                                attendee.visitor_type_id,
                              )
                            }
                            title={translate("eva.main.general.copy")}
                          >
                            <FontAwesomeIcon icon={faCopy} />
                          </StyledAltIconButton>
                        )}
                      </StyledFlexRow>
                    </InputWrapper>

                    {attendee.visitor_type_id && (
                      <>
                        {/* Employee */}
                        {getStepsForVisitorType(attendee.visitor_type_id).find(
                          (step) => step.type === "employee",
                        ) && (
                          <InputWrapper
                            label={translate("eva.main.entities.employee")}
                            error={EVAApiHelper.getResponseError(
                              "employee_id",
                              submitError,
                            )}
                            required
                          >
                            <StyledFlexRow>
                              <StyledFlexItem>
                                <Select
                                  options={employees.map((employee) => ({
                                    value: employee.id,
                                    label: employee.name,
                                  }))}
                                  nullable={false}
                                  value={attendee.employee_id}
                                  onChange={(value) =>
                                    onEmployeeChange(value, attendeeIndex)
                                  }
                                />
                              </StyledFlexItem>

                              {checkedAttendeesCount > 1 && (
                                <StyledAltIconButton
                                  onClick={() =>
                                    onCopyData(
                                      "employee_id",
                                      attendee.employee_id,
                                    )
                                  }
                                  title={translate("eva.main.general.copy")}
                                >
                                  <FontAwesomeIcon icon={faCopy} />
                                </StyledAltIconButton>
                              )}
                            </StyledFlexRow>
                          </InputWrapper>
                        )}

                        {/* Free Fields */}
                        {getStepsForVisitorType(attendee.visitor_type_id)
                          .filter((step) => step.type === "fields")
                          .map((step, stepIndex) =>
                            step.fields.map((row, index) => {
                              const fields = row.filter(
                                (sf) => !sf.hidden_outlook,
                              );

                              return (
                                fields.length > 0 && (
                                  <StyledFlexRow
                                    key={step.id + "_row_" + index}
                                  >
                                    {fields.map((stepField) => (
                                      <StyledFlexItem key={stepField.id}>
                                        <InputWrapper
                                          label={
                                            stepField.field
                                              ? translateEntity(
                                                  stepField.field,
                                                  "label",
                                                )
                                              : translate(
                                                  `eva.main.validation.attributes.${stepField.default_field}`,
                                                )
                                          }
                                          error={EVAApiHelper.getResponseError(
                                            stepField.field
                                              ? `field_${stepField.field.id}`
                                              : stepField.default_field,
                                            submitError,
                                          )}
                                          required={
                                            stepField.required_backoffice
                                          }
                                        >
                                          <FlowFieldInput
                                            flowField={stepField}
                                            value={
                                              attendee[
                                                stepField.field
                                                  ? `field_${stepField.field.id}`
                                                  : stepField.default_field
                                              ]
                                            }
                                            onChange={(stepField, value) =>
                                              onFieldChange(
                                                stepField,
                                                value,
                                                attendeeIndex,
                                              )
                                            }
                                          />
                                        </InputWrapper>
                                      </StyledFlexItem>
                                    ))}
                                  </StyledFlexRow>
                                )
                              );
                            }),
                          )}

                        {/* Returning */}
                        <InputWrapper
                          label={translate(
                            "eva.main.validation.attributes.returning",
                          )}
                          required
                          error={EVAApiHelper.getResponseError(
                            "returning",
                            submitError,
                          )}
                        >
                          <Select
                            value={attendee.returning}
                            options={[
                              {
                                value: true,
                                label: translate("eva.main.general.yes"),
                              },
                              {
                                value: false,
                                label: translate("eva.main.general.no"),
                              },
                            ]}
                            onChange={(value) =>
                              onReturningChange(value, attendeeIndex)
                            }
                          />
                        </InputWrapper>

                        {attendee.returning && (
                          <InputWrapper
                            label={translate(
                              "eva.main.validation.attributes.expected_end_date",
                            )}
                            error={EVAApiHelper.getResponseError(
                              "expected_end_date",
                              submitError,
                            )}
                            required={true}
                          >
                            <StyledInput
                              type="date"
                              value={attendee.expected_end_date}
                              onChange={(e) =>
                                onExpectedEndDateChange(
                                  e.target.value,
                                  attendeeIndex,
                                )
                              }
                            />
                          </InputWrapper>
                        )}
                      </>
                    )}

                    <hr
                      style={{
                        marginBottom: theme.spacing.normal,
                        marginTop: theme.spacing.normal,
                      }}
                    />
                  </>
                )}
              </StyledFlexColumn>
            ))}
          </StyledFlexColumn>
        </FormContainer>
      ) : (
        <OutlookNoPermission>
          {translate("eva.main.auth.no_permission")}
        </OutlookNoPermission>
      )}

      <OutlookVersionWrapper>
        <Version />

        <StyledAltIconButton
          onClick={() => authContext.logout()}
          title={translate("eva.main.auth.logout")}
        >
          <FontAwesomeIcon icon={faSignOut} />
        </StyledAltIconButton>
      </OutlookVersionWrapper>
    </OverlayLoader>
  );
}

export default OutlookPageView;
